/**
 * Weather lookup for the daily/weekly/monthly tick lists.
 *
 * Uses the free Open-Meteo API — no API key, generous fair-use, EU-hosted.
 * Auto-populates the weather chip selection on the tick form based on
 * the site's geofence centre + current time. User can adjust before
 * saving.
 *
 * Why we capture this: when an incident happens on site, "what were the
 * conditions" is one of the first questions HSE / insurance / a tribunal
 * will ask. Having weather baked into every daily check turns into a
 * defensible audit trail.
 *
 *   const w = await fetchWeather({ lat: 53.99, lng: -1.54 });
 *   // → { temperatureC: 12, windMph: 18, rainMm: 0.4, weatherCode: 61,
 *   //     chips: ['rain', 'cloudy'] }
 */

export type WeatherChip =
  | 'sunny'
  | 'cloudy'
  | 'rain'
  | 'wind'
  | 'cold'
  | 'hot';

export interface WeatherChipDef {
  key: WeatherChip;
  label: string;
  icon: string;
}

/** Display labels + icons for each chip. Order matters — used to render. */
export const WEATHER_CHIPS: WeatherChipDef[] = [
  { key: 'sunny',  label: 'Sunny / dry',   icon: '☀' },
  { key: 'cloudy', label: 'Overcast',      icon: '☁' },
  { key: 'rain',   label: 'Rain',          icon: '🌧' },
  { key: 'wind',   label: 'High wind',     icon: '💨' },
  { key: 'cold',   label: 'Cold / frost',  icon: '❄' },
  { key: 'hot',    label: 'Hot',           icon: '🔥' },
];

export interface WeatherSnapshot {
  /** Air temperature in °C. */
  temperatureC: number | null;
  /** Sustained wind speed at 10m in mph. */
  windMph: number | null;
  /** Wind gusts in mph. */
  gustsMph: number | null;
  /** Precipitation in the last hour, in mm. */
  rainMm: number | null;
  /** WMO weather code — see https://open-meteo.com/en/docs */
  weatherCode: number | null;
  /** The chip keys we'd auto-tick based on the above. */
  chips: WeatherChip[];
  /** Source attribution — appears on the PDF. */
  fetchedAt: string;
}

/**
 * Map a WMO weather code to a chip. See open-meteo.com/en/docs for the
 * full table — we collapse it into our six categories.
 */
function chipsForWeatherCode(code: number | null): WeatherChip[] {
  if (code == null) return [];
  // 0 clear, 1 mostly clear → sunny
  if (code <= 1) return ['sunny'];
  // 2 partly cloudy, 3 overcast → cloudy
  if (code <= 3) return ['cloudy'];
  // 45-48 fog → cloudy
  if (code >= 45 && code <= 48) return ['cloudy'];
  // 51-67 drizzle/rain, 80-82 rain showers → rain
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return ['rain'];
  // 71-77 snowfall, 85-86 snow showers → cold (snow)
  if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return ['cold'];
  // 95-99 thunderstorm → rain (with wind likely added below)
  if (code >= 95) return ['rain'];
  return [];
}

/**
 * Apply the temperature / wind overlays. These can be ticked alongside
 * the rain/sunny chips — a day can be both "rain" and "wind".
 */
function applyOverlays(
  base: WeatherChip[],
  temperatureC: number | null,
  windMph: number | null,
  gustsMph: number | null,
): WeatherChip[] {
  const out = new Set(base);
  // Cold: ≤3°C is when frost starts to bite mortar, scaffolds, etc.
  if (temperatureC != null && temperatureC <= 3) out.add('cold');
  // Hot: >26°C is the rough threshold where heat stress becomes a real
  // consideration for outdoor manual work in the UK.
  if (temperatureC != null && temperatureC > 26) out.add('hot');
  // Wind: >23mph sustained or >30mph gust is when working-at-height
  // gets restricted under HSE / scaffold guidance.
  if ((windMph != null && windMph > 23) || (gustsMph != null && gustsMph > 30)) {
    out.add('wind');
  }
  return Array.from(out);
}

/**
 * Fetch the current conditions at a lat/lng. Returns null if the call
 * fails — we tolerate it gracefully because weather is auxiliary; the
 * tick form still saves without it.
 */
export async function fetchWeather(
  loc: { lat: number; lng: number },
): Promise<WeatherSnapshot | null> {
  try {
    // Open-Meteo: current conditions + UK units (mph for wind, mm for
    // rain). The `current` query is the cheapest endpoint they offer.
    const url = new URL('https://api.open-meteo.com/v1/forecast');
    url.searchParams.set('latitude', String(loc.lat));
    url.searchParams.set('longitude', String(loc.lng));
    url.searchParams.set(
      'current',
      'temperature_2m,precipitation,wind_speed_10m,wind_gusts_10m,weather_code',
    );
    url.searchParams.set('wind_speed_unit', 'mph');
    url.searchParams.set('timezone', 'Europe/London');

    const res = await fetch(url.toString());
    if (!res.ok) return null;
    const data = await res.json();
    const c = data?.current ?? {};
    const temperatureC: number | null = typeof c.temperature_2m === 'number' ? c.temperature_2m : null;
    const windMph:      number | null = typeof c.wind_speed_10m === 'number' ? c.wind_speed_10m : null;
    const gustsMph:     number | null = typeof c.wind_gusts_10m === 'number' ? c.wind_gusts_10m : null;
    const rainMm:       number | null = typeof c.precipitation === 'number' ? c.precipitation : null;
    const weatherCode:  number | null = typeof c.weather_code === 'number' ? c.weather_code : null;

    const baseChips = chipsForWeatherCode(weatherCode);
    const chips = applyOverlays(baseChips, temperatureC, windMph, gustsMph);

    return {
      temperatureC,
      windMph,
      gustsMph,
      rainMm,
      weatherCode,
      chips,
      fetchedAt: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Format a snapshot as a one-line summary for the PDF. Skips fields we
 * couldn't capture so the line stays clean.
 */
export function formatWeatherSummary(
  chips: WeatherChip[],
  snapshot: WeatherSnapshot | null,
): string {
  if (chips.length === 0) return '';
  const chipLabels = chips
    .map((k) => WEATHER_CHIPS.find((c) => c.key === k)?.label ?? k)
    .join(', ');
  const stats: string[] = [];
  if (snapshot?.temperatureC != null) stats.push(`${Math.round(snapshot.temperatureC)}°C`);
  if (snapshot?.windMph != null)      stats.push(`${Math.round(snapshot.windMph)} mph wind`);
  if (snapshot?.rainMm != null && snapshot.rainMm > 0) {
    stats.push(`${snapshot.rainMm.toFixed(1)} mm rain`);
  }
  return stats.length > 0 ? `${chipLabels} (${stats.join(' · ')})` : chipLabels;
}
